import React, { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const getToday = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_KEY
);

const Search = () => {
  const [searchInput, setSearchInput] = useState('');
  const [results, setResults] = useState([]);
  const [upcomingSchedule, setUpcomingSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [error, setError] = useState('');
  const [upcomingError, setUpcomingError] = useState('');

  useEffect(() => {
    const loadUpcomingSchedule = async () => {
      setUpcomingLoading(true);
      setUpcomingError('');

      try {
        const { data, error: queryError } = await supabase.rpc('get_upcoming_schedule');

        if (queryError) throw queryError;

        setUpcomingSchedule(data ?? []);
      } catch (err) {
        setUpcomingError('Error fetching upcoming schedule: ' + err.message);
      } finally {
        setUpcomingLoading(false);
      }
    };

    loadUpcomingSchedule();
  }, []);

  const handleSearch = async () => {
    if (!searchInput.trim()) {
      setError('Please enter a person name');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);

    try {
      // Query Supabase with JOIN across three tables
      // Equivalent SQL:
      // SELECT p.name, r.role_name, s.date, s.lesson_title
      // FROM schedule s
      // JOIN persons p ON s.person_id = p.id
      // JOIN roles r ON s.role_id = r.id
      // WHERE p.name ILIKE '%search_input%'
      //   AND s.lesson_title IS NOT NULL
      // ORDER BY s.date ASC
      // Call the Postgres RPC function `get_schedule_by_name` you created in Supabase
      const { data, error: queryError } = await supabase.rpc('get_schedule_by_name', {
        search_text: searchInput,
      });

      if (queryError) throw queryError;

      if (!data || data.length === 0) {
        setError('No schedule found for this person');
      } else {
        // `data` is already flat: [{ name, role_name, scheduled_date, lesson_title }, ...]
        const upcomingResults = data.filter((entry) => {
          const scheduledDate = String(entry.scheduled_date ?? '').slice(0, 10);
          return scheduledDate >= getToday();
        });

        if (upcomingResults.length === 0) {
          setError('No upcoming schedule found for this person');
        } else {
          setResults(upcomingResults);
        }
      }
    } catch (err) {
      setError('Error fetching data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Magusa SDA Schedule</h1>

      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Enter your name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          style={{ padding: '10px', width: '100%', marginBottom: '10px' }}
        />
        <button
          onClick={handleSearch}
          disabled={loading}
          style={{ padding: '10px 20px', cursor: 'pointer' }}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {results.length > 0 && (
        <>
          <h2>Personal Schedule</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ border: '1px solid #ddd', padding: '10px' }}>Person Name</th>
                <th style={{ border: '1px solid #ddd', padding: '10px' }}>Role</th>
                <th style={{ border: '1px solid #ddd', padding: '10px' }}>Date</th>
                <th style={{ border: '1px solid #ddd', padding: '10px' }}>Lesson Title</th>
              </tr>
            </thead>
            <tbody>
              {results.map((entry, i) => (
                <tr key={entry.id ?? `${entry.name}-${entry.scheduled_date}-${i}`}>
                  <td style={{ border: '1px solid #ddd', padding: '10px' }}>{entry.name ?? '-'}</td>
                  <td style={{ border: '1px solid #ddd', padding: '10px' }}>{entry.role_name ?? '-'}</td>
                  <td style={{ border: '1px solid #ddd', padding: '10px' }}>{entry.scheduled_date ?? '-'}</td>
                  <td style={{ border: '1px solid #ddd', padding: '10px' }}>{entry.lesson_title ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="screenshot-reminder" role="status">
            Take a screenshot of your personal schedule
          </p>
        </>
      )}

      <h2>Upcoming Sabbath</h2>
      {upcomingLoading && <p>Loading upcoming schedule...</p>}
      {upcomingError && <p style={{ color: 'red' }}>{upcomingError}</p>}
      {!upcomingLoading && !upcomingError && upcomingSchedule.length === 0 && (
        <p>No upcoming schedule found.</p>
      )}
      {upcomingSchedule.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f0f0f0' }}>
              <th style={{ border: '1px solid #ddd', padding: '10px' }}>Person Name</th>
              <th style={{ border: '1px solid #ddd', padding: '10px' }}>Role</th>
              <th style={{ border: '1px solid #ddd', padding: '10px' }}>Date</th>
              <th style={{ border: '1px solid #ddd', padding: '10px' }}>Lesson Title</th>
            </tr>
          </thead>
          <tbody>
            {upcomingSchedule.map((entry, i) => (
              <tr key={entry.schedule_id ?? `${entry.person_name}-${entry.schedule_date}-${i}`}>
                <td style={{ border: '1px solid #ddd', padding: '10px' }}>{entry.person_name ?? '-'}</td>
                <td style={{ border: '1px solid #ddd', padding: '10px' }}>{entry.role_name ?? '-'}</td>
                <td style={{ border: '1px solid #ddd', padding: '10px' }}>{entry.schedule_date ?? '-'}</td>
                <td style={{ border: '1px solid #ddd', padding: '10px' }}>{entry.lesson_title ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Search;
